/**
 * @file ChangePasswordDialog.tsx
 * @brief Sheet for super-admin-driven password resets via
 *        `POST /api/admin/users/:id/change-password`.
 *
 * @author DES
 * @date 26-05-2026
 * @updated 29-07-2026 — FormSheet + GridBlock chrome
 */

import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Eye, EyeOff, LoaderCircle } from "lucide-react";
import { Avatar } from "@/components/ui/Avatar";
import { Button } from "@/components/ui/Button";
import {
  FormFieldGroup,
  FormSheet,
  FormSheetAlert,
  FormSheetBody,
  FormSheetContent,
  FormSheetDescription,
  FormSheetField,
  FormSheetFields,
  FormSheetFooter,
  FormSheetFooterCell,
  FormSheetFrame,
  FormSheetHeader,
  FormSheetSection,
  FormSheetTitle,
  formSheetActionClass,
} from "@/components/ui/FormSheet";
import { Input } from "@/components/ui/Input";
import { ApiClientError } from "@/shared/api/client";
import { toast } from "@/shared/hooks/useToast";
import { cn } from "@/shared/theme/cn";
import { formatFullName, getInitials } from "@/shared/utils/formatters";
import { useChangeUserPassword } from "../api";
import { ChangeUserPasswordInputSchema, type ChangeUserPasswordInput } from "../schemas";
import type { AdminUser } from "../schemas";

interface ChangePasswordDialogProps {
  user: AdminUser | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ChangePasswordDialog({ user, open, onOpenChange }: ChangePasswordDialogProps) {
  const [show, setShow] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const changePassword = useChangeUserPassword();

  const form = useForm<ChangeUserPasswordInput>({
    resolver: zodResolver(ChangeUserPasswordInputSchema),
    defaultValues: { newPassword: "", confirmPassword: "" },
  });

  useEffect(() => {
    if (open) {
      form.reset();
      setShow(false);
      setServerError(null);
    }
  }, [open, form]);

  if (!user) return null;

  const displayName = formatFullName(user.first_name, user.last_name);
  const headerName = displayName !== "—" && displayName ? displayName : user.email;

  const onSubmit = form.handleSubmit(async (values) => {
    setServerError(null);
    try {
      await changePassword.mutateAsync({
        id: user.id,
        newPassword: values.newPassword,
      });
      toast({
        variant: "success",
        title: "Password changed",
        description: `Tell ${user.email} the new password securely.`,
      });
      onOpenChange(false);
    } catch (err) {
      if (err instanceof ApiClientError) {
        setServerError(err.message);
      } else {
        setServerError("Unexpected error. Please try again.");
      }
    }
  });

  const errors = form.formState.errors;

  return (
    <FormSheet open={open} onOpenChange={onOpenChange}>
      {/* DES 01-10-2026: form standard. Cmd+K palette frame, narrowed to
          max-w-3xl so two fields don't sit in a page-wide frame. */}
      <FormSheetContent side="palette" className="max-w-3xl">
        <form
          onSubmit={onSubmit}
          className="flex min-h-0 flex-1 flex-col overflow-hidden"
          noValidate
        >
          <FormSheetFrame>
            <FormSheetHeader>
              <div className="flex items-center gap-3">
                <Avatar
                  src={user.profile_image_url}
                  initials={getInitials(user.first_name, user.last_name, user.email)}
                  alt={headerName}
                  size="lg"
                />
                <div className="min-w-0">
                  <FormSheetTitle className="truncate">Change password</FormSheetTitle>
                  <FormSheetDescription className="truncate">
                    {headerName} · {user.email}
                  </FormSheetDescription>
                </div>
              </div>
            </FormSheetHeader>

            <FormSheetBody>
              {/* DES 01-10-2026: form standard. The sheet title already says
                  "Change password", so no section title; the two passwords
                  sit side by side. */}
              <FormSheetSection
                hideHeading
                title="Security"
                subtitle="They will need this password on their next sign-in. Min 8 chars · upper + lower + number + symbol · no spaces."
              >
                <FormSheetFields className="sm:grid-cols-2">
                  <FormSheetField>
                    <FormFieldGroup
                      label="New password"
                      error={errors.newPassword?.message}
                      required
                    >
                      <div className="relative">
                        <Input
                          type={show ? "text" : "password"}
                          autoComplete="new-password"
                          className="pr-10"
                          {...form.register("newPassword")}
                        />
                        <button
                          type="button"
                          onClick={() => setShow((v) => !v)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-1 text-muted-foreground hover:text-foreground"
                          aria-label={show ? "Hide password" : "Show password"}
                        >
                          {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </FormFieldGroup>
                  </FormSheetField>
                  <FormSheetField>
                    <FormFieldGroup
                      label="Confirm password"
                      error={errors.confirmPassword?.message}
                      required
                    >
                      <Input
                        type={show ? "text" : "password"}
                        autoComplete="new-password"
                        {...form.register("confirmPassword")}
                      />
                    </FormFieldGroup>
                  </FormSheetField>
                </FormSheetFields>
              </FormSheetSection>

              {serverError ? (
                <FormSheetFields className="grid-cols-1">
                  <FormSheetAlert>{serverError}</FormSheetAlert>
                </FormSheetFields>
              ) : null}
            </FormSheetBody>

            <FormSheetFooter>
              <FormSheetFooterCell>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => onOpenChange(false)}
                  disabled={changePassword.isPending}
                  className={formSheetActionClass}
                >
                  Cancel
                </Button>
              </FormSheetFooterCell>
              <FormSheetFooterCell>
                <Button
                  type="submit"
                  disabled={changePassword.isPending}
                  className={cn(
                    formSheetActionClass,
                    "bg-primary text-primary-foreground hover:bg-primary/90",
                  )}
                >
                  {changePassword.isPending ? (
                    <>
                      <LoaderCircle className="h-4 w-4 animate-spin" />
                      Changing…
                    </>
                  ) : (
                    "Change password"
                  )}
                </Button>
              </FormSheetFooterCell>
            </FormSheetFooter>
          </FormSheetFrame>
        </form>
      </FormSheetContent>
    </FormSheet>
  );
}
